import { useQuery } from '@tanstack/react-query';
import { createFileRoute, Link, useNavigate } from '@tanstack/react-router';
import { useEffect } from 'react';
import { Button } from '@/components/ui/Button';
import { useApi } from '@/lib/api';

/**
 * First-run gate. The user must own at least one domain before reaching the rest
 * of the app, so this page queries `GET /api/domains` and, once a domain exists,
 * forwards to the dashboard (keep that redirect — this CTA only shows
 * pre-domain). With zero domains it shows an empty state whose CTA enters the
 * funnel-creation wizard (FF-21), which starts at the domain step anyway.
 */
export const Route = createFileRoute('/_authenticated/onboarding')({
  component: OnboardingPage,
});

function OnboardingPage() {
  const api = useApi();
  const navigate = useNavigate();

  const { data: domainCount, isPending } = useQuery({
    queryKey: ['domains'],
    queryFn: async () => {
      const res = await api.api.domains.$get();
      if (!res.ok) {
        throw new Error('Failed to load domains');
      }
      const { domains } = await res.json();
      return domains.length;
    },
  });

  // Once the user has a domain, onboarding is complete — move them along.
  useEffect(() => {
    if (domainCount !== undefined && domainCount > 0) {
      void navigate({ to: '/dashboard' });
    }
  }, [domainCount, navigate]);

  if (isPending) {
    return <div className="p-8">Loading…</div>;
  }

  return (
    <main className="flex min-h-svh flex-col items-center justify-center gap-4 p-8 text-center">
      <h1 className="text-2xl font-semibold">Welcome</h1>
      <p className="text-muted-foreground">
        A funnel starts with a domain — the wizard walks you from registration to live ads.
      </p>
      <Link to="/funnels/new">
        <Button>Create your first funnel</Button>
      </Link>
    </main>
  );
}
